'use client';

import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from 'react-use';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';

type TransportNotAvailable = {
  currentTransportNotAvailable: boolean;
  showTransportNotAvailable: boolean;
  transportNotAvailableIds: string[];
  setShowTransportNotAvailable: React.Dispatch<React.SetStateAction<boolean>>;
};

// Exported so Storybook decorators can wrap stories in a Provider without
// pulling in the SWR-dependent default TransportNotAvailableProvider.
export const TransportNotAvailableContext = createContext<TransportNotAvailable | undefined>(undefined);

export default function TransportNotAvailableProvider(props: { children: ReactNode }) {
  const { children } = props;
  const [sessionFlightsNotAvailable, setSessionFlightsNotAvailable] =
    useSessionStorage<string>('flights-not-available');
  const [showTransportNotAvailable, setShowTransportNotAvailable] = useState(false);
  const { data: transportData } = useSelectedTransport();
  const currentTransportId = transportData?.transports?.[0]?.id;
  const transportNotAvailableIds = sessionFlightsNotAvailable ? sessionFlightsNotAvailable.split(',') : [];
  const currentTransportNotAvailable = currentTransportId
    ? transportNotAvailableIds?.includes(currentTransportId)
    : false;

  useEffect(() => {
    if (currentTransportId) {
      if (showTransportNotAvailable && !transportNotAvailableIds.includes(currentTransportId)) {
        setSessionFlightsNotAvailable([...transportNotAvailableIds, currentTransportId].join(','));
        setShowTransportNotAvailable(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTransportId, transportNotAvailableIds, showTransportNotAvailable]);

  const memoizedValue = useMemo(
    () => ({
      showTransportNotAvailable,
      transportNotAvailableIds,
      setShowTransportNotAvailable,
      currentTransportNotAvailable,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showTransportNotAvailable, transportNotAvailableIds, sessionFlightsNotAvailable, currentTransportNotAvailable]
  );

  return (
    <TransportNotAvailableContext.Provider value={memoizedValue}>{children}</TransportNotAvailableContext.Provider>
  );
}

export function useTransportNotAvailable() {
  const context = useContext(TransportNotAvailableContext);
  if (context === undefined) {
    throw new Error('useTransportNotAvailable must be within a TransportNotAvailableContext');
  }
  return context;
}
